import {
  CalendarDaysIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "shared/ui/shadcn/button";
import { clsxMerge } from "shared/lib/helpers";

const BOOKING_STEPS = [
  "Click the button below to open the scheduling page",
  "Select an available time slot that works for the prospect",
  "Set prospect name and email as an invitee info",
  "Complete the booking form in the popup window and close it",
  "Return here to confirm your booking",
];

interface InstructionsStepPropsI {
  meetingTakerName: string;
  contactName: string | undefined;
  showConfirmSection: boolean;
  onOpenSchedulingLink: () => void;
  onCopyLink: () => void;
  onVerifyBooking: () => void;
}

export const InstructionsStep = ({
  meetingTakerName,
  contactName,
  showConfirmSection,
  onOpenSchedulingLink,
  onCopyLink,
  onVerifyBooking,
}: InstructionsStepPropsI) => (
  <div className="space-y-5">
    {/* Hero section */}
    <div className="flex items-center gap-4 rounded-xl bg-gradient-to-r from-base-200 to-base-300/50 p-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-black/10">
        <CalendarDaysIcon className="h-6 w-6 text-base-content" />
      </div>
      <div>
        <h3 className="typography-body-3-medium">
          Book meeting between {meetingTakerName} and{" "}
          {contactName || "prospect"} (prospect)
        </h3>
        <p className="text-sm text-base-content/60">
          Use their scheduling link to find an available time
        </p>
      </div>
    </div>

    {/* Step cards */}
    <div className="space-y-2">
      {BOOKING_STEPS.map((stepText, i) => (
        <div
          key={i}
          className="flex items-start gap-3 rounded-lg border border-base-300 bg-base-100 p-3 transition-colors hover:bg-base-200/50"
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black text-xs font-medium text-white">
            {i + 1}
          </span>
          <p className="pt-0.5 text-base-content/80 typography-body-4">
            {stepText}
          </p>
        </div>
      ))}
    </div>

    {/* Confirmation section - delayed reveal */}
    <div
      className={clsxMerge(
        "overflow-hidden transition-all duration-500 ease-out",
        showConfirmSection
          ? "max-h-40 opacity-100"
          : "pointer-events-none max-h-0 opacity-0"
      )}
    >
      <div
        className={clsxMerge(
          "border-b border-base-200",
          showConfirmSection && "pb-4"
        )}
      >
        <p className="mb-3 text-center text-sm text-base-content/60">
          Finished booking on the external page?
        </p>
        <Button size="lg" className="w-full" onClick={onVerifyBooking}>
          <CheckCircleIcon className="h-4 w-4" />
          I've booked the meeting
        </Button>
      </div>
    </div>

    <section className="flex flex-col gap-2">
      {/* Primary CTA */}
      <Button
        className="w-full"
        variant={showConfirmSection ? "secondary" : "default"}
        onClick={onOpenSchedulingLink}
      >
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        Open scheduling link
      </Button>

      {/* Subtle copy link */}
      <Button
        variant="ghost"
        className="mx-auto w-full text-base-content/50 hover:text-base-content/70"
        onClick={onCopyLink}
      >
        <ClipboardDocumentIcon className="h-4 w-4" />
        Copy link instead
      </Button>
    </section>
  </div>
);
